import React, { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Clock, Send, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const axios = require('axios').default;
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      toast.success("Message envoyé avec succès!", {
        description: "Nous vous contacterons dans les plus brefs délais."
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Erreur:', error);
      toast.error("Erreur", {
        description: "Une erreur est survenue. Veuillez réessayer."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-5 h-5" />,
      title: "Téléphone",
      content: "(450) 359-9394",
      link: "tel:+14503599394"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Email",
      content: "info@innova-solution.ca",
      link: "mailto:info@innova-solution.ca"
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: "Adresse",
      content: "855 Ch. du Grand Bernier N, Suite 300, Saint-Jean-sur-Richelieu, QC",
      link: null
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Heures d'ouverture",
      content: "Lun-Ven: 8h-18h | Support 24/7",
      link: null
    }
  ];

  return (
    <section id="contact" className="py-24 relative overflow-hidden bg-[#0A0A0A]">
      <div className="absolute inset-0 network-lines opacity-5"></div>
      <div className="absolute top-0 left-1/2 w-96 h-96 bg-[#FF6B35] opacity-5 blur-[150px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
            Contactez-nous
          </span>
          <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
            Prêt à<span className="text-[#FF6B35]"> Démarrer?</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Parlez à nos experts et découvrez comment nous pouvons transformer votre infrastructure technologique
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Left: Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="space-y-6 mb-8">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-[#121212]/50 border border-white/5 rounded-xl hover:border-[#FF6B35]/30 transition-colors">
                  <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center text-[#FF6B35] flex-shrink-0">
                    {info.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-sm text-gray-400">{info.title}</h4>
                    {info.link ? (
                      <a href={info.link} className="text-white hover:text-[#FF6B35] transition-colors">
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-white">{info.content}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Links */}
            <div className="glass rounded-xl p-6 border border-[#FF6B35]/20">
              <h4 className="font-bold mb-4 text-lg">Besoin d'aide immédiate?</h4>
              <div className="space-y-3">
                <a 
                  href="/supportit" 
                  className="flex items-center justify-between p-3 bg-[#121212]/50 rounded-lg hover:bg-[#FF6B35]/10 transition-colors group"
                >
                  <span>Créer un ticket de support</span>
                  <ArrowRight className="w-4 h-4 text-[#FF6B35] group-hover:translate-x-1 transition-transform" />
                </a>
                <a 
                  href="tel:+14503599394" 
                  className="flex items-center justify-between p-3 bg-[#121212]/50 rounded-lg hover:bg-[#FF6B35]/10 transition-colors group"
                >
                  <span>Appeler maintenant</span>
                  <ArrowRight className="w-4 h-4 text-[#FF6B35] group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/10 rounded-2xl p-8">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nom complet</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] rounded-md px-4 py-3 outline-none transition-colors"
                    placeholder="Votre nom"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] rounded-md px-4 py-3 outline-none transition-colors"
                    placeholder="votre@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] rounded-md px-4 py-3 outline-none transition-colors"
                    placeholder="(450) 123-4567"
                  />
                </div>

                {/* Service */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Service</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] rounded-md px-4 py-3 outline-none transition-colors"
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="Infrastructure TI">Infrastructure TI</option>
                    <option value="Téléphonie VoIP">Téléphonie VoIP</option>
                    <option value="Cybersécurité">Cybersécurité</option>
                    <option value="Innova-Pay">Innova-Pay</option>
                    <option value="Gestimotel">Gestimotel</option>
                    <option value="Support technique">Support technique</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] rounded-md px-4 py-3 outline-none transition-colors resize-none"
                    placeholder="Décrivez votre projet ou vos besoins..."
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF6B35] text-white font-semibold rounded-md px-8 py-4 hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      Envoyer le message
                      <Send className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
